import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getConsultas, marcarLeida } from "../../firebase/consultas";
import logo from "../../logo.png";

const VERDE = "#1a9e2a";
const AZUL = "#1a3c5e";

function fechaJS(fecha) {
  if (!fecha) return null;
  if (typeof fecha.toDate === "function") return fecha.toDate();
  if (fecha instanceof Date) return fecha;
  return new Date(fecha);
}

function formatearFecha(fecha) {
  const d = fechaJS(fecha);
  if (!d) return "";
  return d.toLocaleDateString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function tiempoRelativo(fecha) {
  const d = fechaJS(fecha);
  if (!d) return "";
  const diff = Date.now() - d.getTime();
  const seg = Math.floor(diff / 1000);
  const min = Math.floor(seg / 60);
  const hs = Math.floor(min / 60);
  const dias = Math.floor(hs / 24);
  if (seg < 60) return "hace un momento";
  if (min < 60) return "hace " + min + " min";
  if (hs < 24) return "hace " + hs + " h";
  if (dias < 7) return "hace " + dias + " " + (dias === 1 ? "dia" : "dias");
  return formatearFecha(fecha).split(",")[0];
}

function esHoy(fecha) {
  const d = fechaJS(fecha);
  if (!d) return false;
  const hoy = new Date();
  return d.getDate() === hoy.getDate() && d.getMonth() === hoy.getMonth() && d.getFullYear() === hoy.getFullYear();
}

function inicialesDe(nombre) {
  if (!nombre) return "?";
  const partes = nombre.trim().split(/\s+/);
  const a = partes[0] ? partes[0][0] : "";
  const b = partes[1] ? partes[1][0] : "";
  return (a + b).toUpperCase();
}

function colorAvatar(nombre) {
  const colores = ["#1a9e2a", "#1a3c5e", "#7c3aed", "#ea580c", "#0891b2", "#db2777", "#059669", "#dc2626"];
  let suma = 0;
  for (let i = 0; i < (nombre || "").length; i++) suma += nombre.charCodeAt(i);
  return colores[suma % colores.length];
}

export default function Consultas() {
  const navigate = useNavigate();
  const [consultas, setConsultas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState("todas");
  const [busqueda, setBusqueda] = useState("");
  const [expandida, setExpandida] = useState(null);

  async function cargar() {
    setCargando(true);
    const data = await getConsultas();
    setConsultas(data || []);
    setCargando(false);
  }

  useEffect(() => { cargar(); }, []);

  async function handleMarcarLeida(id) {
    await marcarLeida(id);
    cargar();
  }

  function abrirWhatsapp(telefono, nombre) {
    if (!telefono) return;
    const tel = telefono.replace(/\D/g, "");
    const txt = "Hola " + (nombre || "") + ", soy de Confalonieri Propiedades. Te respondo tu consulta:";
    window.open("https://wa.me/" + tel + "?text=" + encodeURIComponent(txt), "_blank", "noopener,noreferrer");
  }

  function copiarEmail(email) {
    if (!email) return;
    navigator.clipboard.writeText(email);
  }

  const stats = useMemo(() => ({
    total: consultas.length,
    sinLeer: consultas.filter((c) => !c.leida).length,
    leidas: consultas.filter((c) => c.leida).length,
    hoy: consultas.filter((c) => esHoy(c.fecha)).length,
  }), [consultas]);

  const filtradas = useMemo(() => {
    let lista = [...consultas];

    // Ordenar por fecha desc
    lista.sort((a, b) => {
      const da = fechaJS(a.fecha);
      const db = fechaJS(b.fecha);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db.getTime() - da.getTime();
    });

    if (filtro === "sin-leer") lista = lista.filter((c) => !c.leida);
    if (filtro === "leidas") lista = lista.filter((c) => c.leida);

    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      lista = lista.filter((c) =>
        (c.nombre || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.telefono || "").toLowerCase().includes(q) ||
        (c.mensaje || "").toLowerCase().includes(q) ||
        (c.propiedadTitulo || "").toLowerCase().includes(q)
      );
    }

    return lista;
  }, [consultas, filtro, busqueda]);

  return (
    <div style={{ minHeight: "100vh", background: "#f4f4f4", fontFamily: "system-ui, sans-serif" }}>

      <nav style={{
        background: "#fff",
        borderBottom: "1px solid #e5e5e5",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
        gap: "12px",
      }}>
        <img
          src={logo}
          alt="Confalonieri Propiedades"
          onClick={() => navigate("/admin")}
          style={{ height: "40px", cursor: "pointer" }}
        />
        <button
          onClick={() => navigate("/admin")}
          style={{
            background: "none",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px 14px",
            color: "#6b7280",
            fontSize: "13px",
            cursor: "pointer",
            fontWeight: "600",
          }}
        >
          Volver al panel
        </button>
      </nav>

      <div style={{ height: "4px", background: VERDE }} />

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "32px 20px" }}>

        {/* TITULO */}
        <div style={{ marginBottom: "24px" }}>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: AZUL, margin: 0, fontFamily: "Georgia, serif" }}>
            Consultas
          </h1>
          <p style={{ color: "#6b7280", fontSize: "14px", margin: "6px 0 0 0" }}>
            {cargando ? "Cargando consultas..." : "Mensajes recibidos a traves del sitio web"}
          </p>
        </div>

        {/* STATS */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "12px",
          marginBottom: "24px",
        }}>
          <TarjetaStat label="Total" valor={stats.total} color={AZUL} />
          <TarjetaStat label="Sin leer" valor={stats.sinLeer} color="#dc2626" destacar={stats.sinLeer > 0} />
          <TarjetaStat label="Leidas" valor={stats.leidas} color="#15803d" />
          <TarjetaStat label="De hoy" valor={stats.hoy} color="#7c3aed" />
        </div>

        {/* CONTROLES */}
        <div style={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e5e7eb",
          padding: "14px",
          marginBottom: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, email, telefono, mensaje o propiedad..."
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              fontSize: "14px",
              outline: "none",
              boxSizing: "border-box",
              background: "#f9fafb",
            }}
            onFocus={(e) => { e.target.style.borderColor = VERDE; e.target.style.background = "#fff"; }}
            onBlur={(e) => { e.target.style.borderColor = "#e5e7eb"; e.target.style.background = "#f9fafb"; }}
          />

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <BotonFiltro activo={filtro === "todas"} onClick={() => setFiltro("todas")} label="Todas" cuenta={stats.total} />
            <BotonFiltro activo={filtro === "sin-leer"} onClick={() => setFiltro("sin-leer")} label="Sin leer" cuenta={stats.sinLeer} color="#dc2626" />
            <BotonFiltro activo={filtro === "leidas"} onClick={() => setFiltro("leidas")} label="Leidas" cuenta={stats.leidas} />
          </div>
        </div>

        {/* LISTA */}
        {cargando ? (
          <div style={{ background: "#fff", borderRadius: "14px", padding: "40px", textAlign: "center", border: "1px solid #e5e7eb" }}>
            <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>Cargando consultas...</p>
          </div>
        ) : filtradas.length === 0 ? (
          <div style={{ background: "#fff", borderRadius: "14px", padding: "60px 20px", textAlign: "center", border: "1px solid #e5e7eb" }}>
            <div style={{
              width: "60px",
              height: "60px",
              background: "#f3f4f6",
              borderRadius: "50%",
              margin: "0 auto 16px auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "26px",
            }}>
              {busqueda ? "🔍" : filtro === "sin-leer" ? "✓" : "✉"}
            </div>
            <p style={{ color: "#374151", fontSize: "15px", fontWeight: "600", margin: "0 0 6px 0" }}>
              {busqueda
                ? "No hay resultados para tu busqueda"
                : filtro === "sin-leer"
                  ? "No tenes consultas sin leer"
                  : filtro === "leidas"
                    ? "Todavia no marcaste consultas como leidas"
                    : "Todavia no hay consultas"}
            </p>
            <p style={{ color: "#9ca3af", fontSize: "13px", margin: 0 }}>
              {busqueda ? "Probá con otros terminos" : "Las consultas que envien por el sitio van a aparecer aca"}
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {filtradas.map((c) => (
              <TarjetaConsulta
                key={c.id}
                consulta={c}
                expandida={expandida === c.id}
                onToggle={() => setExpandida(expandida === c.id ? null : c.id)}
                onMarcarLeida={() => handleMarcarLeida(c.id)}
                onWhatsapp={() => abrirWhatsapp(c.telefono, c.nombre)}
                onCopiarEmail={() => copiarEmail(c.email)}
              />
            ))}
          </div>
        )}

      </div>

      <footer style={{
        borderTop: "3px solid " + VERDE,
        padding: "20px",
        textAlign: "center",
        color: "#9ca3af",
        fontSize: "12px",
        background: "#fff",
        marginTop: "40px",
      }}>
        Confalonieri Propiedades - Panel de administracion
      </footer>

    </div>
  );
}

function TarjetaStat({ label, valor, color, destacar }) {
  return (
    <div style={{
      background: destacar ? "#fef2f2" : "#fff",
      border: "1px solid " + (destacar ? "#fecaca" : "#e5e7eb"),
      borderRadius: "14px",
      padding: "16px 18px",
      transition: "transform 0.15s",
    }}>
      <p style={{ color: "#6b7280", fontSize: "11px", margin: 0, fontWeight: "700", letterSpacing: "0.5px", textTransform: "uppercase" }}>
        {label}
      </p>
      <p style={{ fontSize: "26px", fontWeight: "800", color: color, margin: "6px 0 0 0", lineHeight: 1 }}>
        {valor}
      </p>
    </div>
  );
}

function BotonFiltro({ activo, onClick, label, cuenta, color }) {
  const baseColor = color || "#1a9e2a";
  return (
    <button
      onClick={onClick}
      style={{
        background: activo ? baseColor : "#fff",
        color: activo ? "#fff" : "#374151",
        border: "1px solid " + (activo ? baseColor : "#e5e7eb"),
        borderRadius: "20px",
        padding: "6px 14px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        transition: "all 0.15s",
      }}
    >
      {label}
      <span style={{
        background: activo ? "rgba(255,255,255,0.25)" : "#f3f4f6",
        color: activo ? "#fff" : "#6b7280",
        fontSize: "11px",
        fontWeight: "700",
        padding: "1px 8px",
        borderRadius: "12px",
        minWidth: "20px",
        textAlign: "center",
      }}>
        {cuenta}
      </span>
    </button>
  );
}

function TarjetaConsulta({ consulta, expandida, onToggle, onMarcarLeida, onWhatsapp, onCopiarEmail }) {
  const c = consulta;
  const sinLeer = !c.leida;
  const colorAv = colorAvatar(c.nombre || "");

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "14px",
        border: "1px solid " + (sinLeer ? "#fecaca" : "#e5e7eb"),
        borderLeft: "4px solid " + (sinLeer ? "#dc2626" : "#e5e7eb"),
        overflow: "hidden",
        transition: "box-shadow 0.15s",
        boxShadow: sinLeer ? "0 1px 3px rgba(220,38,38,0.08)" : "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      {/* CABECERA */}
      <div
        onClick={onToggle}
        style={{
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          cursor: "pointer",
        }}
      >
        <div style={{
          width: "44px",
          height: "44px",
          borderRadius: "50%",
          background: colorAv,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "700",
          fontSize: "15px",
          flexShrink: 0,
        }}>
          {inicialesDe(c.nombre)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <p style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: "#111827" }}>
              {c.nombre || "Sin nombre"}
            </p>
            {sinLeer && (
              <span style={{
                background: "#dc2626",
                color: "#fff",
                fontSize: "10px",
                fontWeight: "700",
                padding: "2px 8px",
                borderRadius: "20px",
                letterSpacing: "0.5px",
              }}>
                NUEVA
              </span>
            )}
          </div>
          <p style={{
            margin: "2px 0 0 0",
            fontSize: "12px",
            color: "#6b7280",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {(c.mensaje || "").substring(0, 90)}{(c.mensaje || "").length > 90 ? "..." : ""}
          </p>
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <p style={{ margin: 0, fontSize: "11px", color: "#9ca3af", fontWeight: "600" }}>
            {tiempoRelativo(c.fecha)}
          </p>
          <p style={{ margin: "2px 0 0 0", fontSize: "16px", color: "#9ca3af", lineHeight: 1 }}>
            {expandida ? "▴" : "▾"}
          </p>
        </div>
      </div>

      {/* DETALLE */}
      {expandida && (
        <div style={{ padding: "0 16px 16px 16px", borderTop: "1px solid #f3f4f6" }}>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "10px",
            margin: "14px 0",
          }}>
            {c.email && (
              <DatoContacto label="Email" valor={c.email} accion="Copiar" onAccion={onCopiarEmail} />
            )}
            {c.telefono && (
              <DatoContacto label="Telefono" valor={c.telefono} />
            )}
            {c.fecha && (
              <DatoContacto label="Recibida" valor={formatearFecha(c.fecha)} />
            )}
          </div>

          {c.propiedadTitulo && (
            <div style={{
              background: "#eff6ff",
              border: "1px solid #dbeafe",
              borderRadius: "10px",
              padding: "10px 14px",
              marginBottom: "14px",
            }}>
              <p style={{ margin: 0, fontSize: "11px", fontWeight: "700", color: "#1d4ed8", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                Consulta sobre
              </p>
              <p style={{ margin: "2px 0 0 0", fontSize: "14px", fontWeight: "600", color: "#1e3a8a" }}>
                {c.propiedadTitulo}
              </p>
            </div>
          )}

          <div style={{
            background: "#f9fafb",
            borderRadius: "10px",
            padding: "14px 16px",
            border: "1px solid #f3f4f6",
            marginBottom: "14px",
          }}>
            <p style={{ margin: 0, fontSize: "11px", fontWeight: "700", color: "#6b7280", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "6px" }}>
              Mensaje
            </p>
            <p style={{ margin: 0, fontSize: "14px", color: "#374151", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {c.mensaje || "Sin mensaje"}
            </p>
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {c.telefono && (
              <button
                onClick={(e) => { e.stopPropagation(); onWhatsapp(); }}
                style={{
                  background: "#25d366",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 14px",
                  fontSize: "13px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                Responder por WhatsApp
              </button>
            )}
            {sinLeer && (
              <button
                onClick={(e) => { e.stopPropagation(); onMarcarLeida(); }}
                style={{
                  background: "#f0fdf4",
                  color: "#15803d",
                  border: "1px solid #bbf7d0",
                  borderRadius: "8px",
                  padding: "8px 14px",
                  fontSize: "13px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                Marcar como leida
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DatoContacto({ label, valor, accion, onAccion }) {
  return (
    <div style={{
      background: "#f9fafb",
      borderRadius: "10px",
      padding: "10px 12px",
      border: "1px solid #f3f4f6",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <p style={{ margin: 0, fontSize: "10px", fontWeight: "700", color: "#9ca3af", letterSpacing: "0.5px", textTransform: "uppercase" }}>
          {label}
        </p>
        <p style={{
          margin: "2px 0 0 0",
          fontSize: "13px",
          color: "#111827",
          fontWeight: "600",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {valor}
        </p>
      </div>
      {accion && (
        <button
          onClick={(e) => { e.stopPropagation(); onAccion && onAccion(); }}
          style={{
            background: "none",
            border: "1px solid #e5e7eb",
            color: "#6b7280",
            fontSize: "11px",
            fontWeight: "600",
            padding: "4px 10px",
            borderRadius: "6px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          {accion}
        </button>
      )}
    </div>
  );
}
